import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Grid,
  TextField, CardMedia,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  BugReport as BugIcon,
  Build as BuildIcon,
  Lightbulb as FeatureIcon,
  Assignment as AssignmentIcon,
  PriorityHigh as PriorityHighIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Comment as CommentIcon,
  Attachment as AttachmentIcon
} from '@mui/icons-material';
import { apiMultiPart } from '../../../services/api';
import api from '../../../services/api';

const TicketDetailsModal = ({ open, onClose, ticket, updateTicketStatus }) => {
  const [attachments, setAttachments] = useState([]);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    // if (!ticket) return null;
    if (open) {
      fetchAttachments();
    }
  }, [open]);

  if (!ticket) return null;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenImageDialog(true);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
    setOpenImageDialog(false)
  };

  const fetchAttachments = async () => {
    try {
      const promises = ticket.paths.map(path => apiMultiPart.get(`/v1/api/tickets/attachments/${ticket.id}/${path}`, { responseType: 'blob' }));
      const responses = await Promise.all(promises);
      const attachmentData = responses.map(response => URL.createObjectURL(response.data));
      setAttachments(attachmentData);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    }
  };


  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High':
      case 'Critical':
        return <PriorityHighIcon color="error" />;
      default:
        return <PriorityHighIcon />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'BUG':
        return <BugIcon color="error" />;
      case 'Feature Request':
        return <FeatureIcon color="primary" />;
      case 'Technical Issue':
        return <BuildIcon color="action" />;
      default:
        return <AssignmentIcon />;
    }
  };

  const handleTicketStatusChange = async () => {
  
    let status = '';
    if (ticket.status === 'OPEN') {
      status = 'IN_PROGRESS'
    } else if (ticket.status === 'IN_PROGRESS') {
      status = 'CLOSED'
      // updateTicketStatus(ticket.id, 'CLOSED');
    } else {
      status = 'OPEN'
    }

    try {
      const response = await api.put(`/v1/api/tickets/ticket?id=${ticket.id}&&status=${status}`);

      ticket.status = response.data.status
      updateTicketStatus(ticket.id, status);
      showSnackbar('Ticket status updated successfully', 'success');
      // setAssignOpen(false);
      // handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to assign ticket', 'error');
    }


  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth >
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        py: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600}>
            Ticket Details
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            {getTypeIcon(ticket.type)}
            <Typography variant="h4">{ticket.title}</Typography>
          </Stack>

          <Stack direction="row" spacing={2} mb={3}>
            <Chip
              label={ticket.status}
              color={
                ticket.status === 'OPEN' ? 'info' :
                  ticket.status === 'In Progress' ? 'primary' :
                    ticket.status === 'Resolved' ? 'success' : 'default'
              }
              sx={{ textTransform: 'capitalize' }}
            />
            <Chip
              icon={getPriorityIcon(ticket.priority)}
              label={ticket.priority}
              variant="outlined"
            />
            <Chip
              label={ticket.type}
              variant="outlined"
            />
          </Stack>

          <Typography variant="body1" paragraph>
            {ticket.description}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Ticket Information
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon><PersonIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Created By" secondary={ticket.createdBy} />
                </ListItem>

                <ListItem>
                  <ListItemIcon><PersonIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Assigned To" secondary={ticket.assignedTo || 'Unassigned'} />
                </ListItem>

                <ListItem>
                  <ListItemIcon><EventIcon color="action" /></ListItemIcon>
                  <ListItemText
                    primary="Created At"
                    secondary={new Date(ticket.createdAt).toLocaleString()}
                  />
                </ListItem>

                {ticket.dueDate && (
                  <ListItem>
                    <ListItemIcon><EventIcon color="action" /></ListItemIcon>
                    <ListItemText
                      primary="Due Date"
                      secondary={new Date(ticket.dueDate).toLocaleDateString()}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            {ticket.paths?.length > 0 && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Attachments ({ticket.paths?.length})
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {attachments.map((image, index) => (
                    <CardMedia
                      key={index}
                      component="img"
                      image={image}
                      onClick={() => handleImageClick(image)}
                      sx={{
                        width: 200,
                        height: 150,
                        mr: 1,
                        mb: 1,
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Box>

                <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="lg" fullWidth>
                  <DialogContent>
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        aria-label="close"
                        onClick={handleCloseImage}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                          color: (theme) => theme.palette.grey[500],
                          zIndex: 1
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                      <Box
                        component="img"
                        src={selectedImage}
                        alt={selectedImage}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '80vh',
                          objectFit: 'contain'
                        }}
                      >
                      </Box>
                    </Box>
                  </DialogContent>

                </Dialog>

                <List dense>
                  {ticket.attachments?.map((attachment) => (
                    <ListItem key={attachment.id}>
                      <ListItemIcon><AttachmentIcon color="action" /></ListItemIcon>
                      <ListItemText
                        primary={
                          <Link href={attachment} target="_blank" rel="noopener">
                            {attachment} {attachment.name}
                          </Link>
                        }
                        secondary={`Uploaded by User on ${new Date(attachment.uploadedAt).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Comments ({ticket.comments?.length})
          </Typography>

          {ticket.comments?.length > 0 ? (
            <List>
              {ticket.comments.map((comment) => (
                <ListItem key={comment.id}>
                  <ListItemIcon>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {comment.user.charAt(0)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={comment.user}
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {comment.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.createdAt).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No comments yet
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add a comment..."
              variant="outlined"
            />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<CommentIcon />}>
                Add Comment
              </Button>
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          sx={{ borderRadius: 2 }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color={ticket.status === 'OPEN' ? 'secondary' : ticket.status === 'IN_PROGRESS' ? 'success' : 'error'}
          startIcon={<CheckIcon />}
          sx={{ borderRadius: 2 }}
          onClick={handleTicketStatusChange}
        >
          {ticket.status === 'OPEN' ? 'Mark as In Progress' : ticket.status === 'IN_PROGRESS' ? 'Close' : 'Reopen Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDetailsModal;